import React, { useState } from 'react';
import { Box, Button, Divider, Link, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { merge, get, has } from 'lodash';
import ReactHCaptcha from '@hcaptcha/react-hcaptcha';
import { useNotifications } from '@toolpad/core/useNotifications';

import AuthLayout from '../../../Layout/AuthLayout/AuthLayout';
import MetaTags from '../../../Components/MetaTags';
import ButtonWithLoader from '../../../Components/ButtonWithLoader';
import { login, sso } from '../../../Services/Api';
import { generateErrorsForWeb } from '../../../Services/ApiErrorHelpers';
import { spacePath } from '../../../Utils/routes';
import useSpace from '../../../Hooks/useSpace';
import OAuthButtons from '../../../Components/Auth/OAuthButtons/OAuthButtons';
import EmailFieldController from '../../../Components/Auth/EmailFieldController/EmailFieldController';
import PasswordFieldController from '../../../Components/Auth/PasswordFieldController/PasswordFieldController';
import { hasOAuth } from '../../../Utils/OAuth';
import AnalyticsService from '../../../Services/AnalyticsService';
import HCaptcha from '../../../Components/Auth/HCaptcha';
import { notify } from '../../../Utils/NotificationUtils';
import useCurrentUser from '../../../Hooks/useCurrentUser';

function SignIn() {
  const intl = useIntl();
  const notifications = useNotifications();
  const captchaRef = React.useRef<ReactHCaptcha>(null);
  const { space, refetch: refetchSpace } = useSpace();
  const { refetch: refetchCurrentUser } = useCurrentUser();
  const params = new URLSearchParams(window.location.search);
  const invitationCode = params.get('invitation_code');
  const redirectTo = params.get('redirect_to');

  const [apiErrors, setApiErrors] = useState({});
  const [fetching, setFetching] = useState(false);

  const { control, handleSubmit, watch, formState } = useForm({
    mode: 'onSubmit',
    criteriaMode: 'all',
    defaultValues: {
      email: '',
      password: '',
      hcaptcha_token: null,
    },
  });
  const errors = merge(formState.errors, apiErrors);

  const handleSignIn = async (payload: any, provider = 'email') => {
    // Call omniauth endpoint if the provider is not email
    const endpoint = provider === 'email' ? login(payload) : sso(provider, payload);

    setFetching(true);
    const response = await endpoint;
    setFetching(false);

    if (response.status === 201 && provider !== 'email') {
      notify(
        notifications,
        intl.formatMessage({ id: 'AUTH.ERRORS.NO_ASSOCIATED_ACCOUNT' }),
        'error',
      );

      return;
    }

    if (response.ok) {
      AnalyticsService.track('Authentication', 'Sign In');
      notify(
        notifications,
        intl.formatMessage({ id: 'AUTH.HAS_SIGNED_IN' }, { spaceName: space.name }),
      );

      await refetchSpace();
      refetchCurrentUser();

      // redirection is handled in GuestRoute
      return;
    }

    const generatedErrors = generateErrorsForWeb(response.data);

    if (response.status === 403) {
      notify(notifications, response.data.errors[0], 'error');

      return;
    }

    setApiErrors(generatedErrors);
  };

  const handleFormSubmit = async (data: any) => {
    const payload = {
      // NOTE: we need to send the email and password in the user object for Devise
      user: {
        email: data.email,
        password: data.password,
      },
      ...data,
      invitation_code: invitationCode,
      provider_name: 'email',
      space_id: space.id,
    };

    handleSignIn(payload);

    if (captchaRef.current) {
      // NOTE: we need to reset the captcha after each submit
      // because the API will already consume the previous token
      captchaRef.current.resetCaptcha();
    }
  };

  const handleSsoAuth = async (providerName: any, ssoPayload = {}) => {
    const payload = {
      invitation_code: invitationCode,
      space_id: space.id,
      ...ssoPayload,
    };

    handleSignIn(payload, providerName);
  };

  return (
    <>
      <MetaTags
        title={`${intl.formatMessage({ id: 'AUTH.PAGE_TITLE' })} | ${space.name}`}
        noindex
      />

      <AuthLayout>
        <Typography component="h1" variant="h4" sx={{ mb: 5 }}>
          <FormattedMessage id="AUTH.LOGIN" />
        </Typography>

        {hasOAuth(space) && (
          <Box sx={{ mb: 4 }}>
            <OAuthButtons space={space} onAuth={handleSsoAuth} mb={4} />

            <Divider>
              <FormattedMessage id="OR" />
            </Divider>
          </Box>
        )}
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <EmailFieldController
            control={control}
            name="email"
            error={has(errors, 'email.message')}
            required
          />
          <Box sx={{ my: 2 }}>
            <PasswordFieldController
              control={control}
              name="password"
              error={has(errors, 'password.message')}
              helperText={get(errors, 'password.message')}
              password={watch('password')}
              isSignUp={false}
              required
            />
          </Box>

          <Link component={RouterLink} to={spacePath('password-reset-request')} underline="hover">
            <FormattedMessage id="AUTH.FORGOT_PASSWORD" />
          </Link>
          <Box sx={{ mt: 3 }}>
            <HCaptcha
              ref={captchaRef}
              control={control}
              error={!!get(errors, 'hcaptcha_token.message')}
              helperText={get(errors, 'hcaptcha_token.message')}
            />
          </Box>
          <Box sx={{ my: 4 }}>
            <ButtonWithLoader
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              disabled={fetching}
              isFetching={fetching}
              type="submit"
            >
              <FormattedMessage id="AUTH.LOGIN" />
            </ButtonWithLoader>
          </Box>

          <Typography variant="body1">
            <FormattedMessage id="AUTH.NO_ACCOUNT" />
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              color="primary"
              size="large"
              variant="outlined"
              component={RouterLink}
              to={spacePath('signup', {
                invitation_code: invitationCode,
                redirect_to: redirectTo,
              })}
            >
              <FormattedMessage id="AUTH.SIGNUP" />
            </Button>
          </Box>
        </form>
      </AuthLayout>
    </>
  );
}

export default SignIn;
